import { getEffectiveRefundStatus } from './refunds';
import type { OrderHistoryEntry } from '../types';

/** The order fields the status presenters read. */
export type OrderStatusFields = Pick<
  OrderHistoryEntry,
  'refund_status' | 'status' | 'tracking_status' | 'tracking_status_label'
>;

/**
 * Map an order to its `.status-badge` variant class. Refund state takes
 * precedence over tracking/purchase state.
 */
export function getStatusBadgeClass(order: OrderStatusFields): string {
  const refundStatus = getEffectiveRefundStatus(order);
  if (refundStatus === 'submitted') return 'warning';
  if (refundStatus === 'refunded') return 'success';
  if (refundStatus === 'rejected') return 'error';
  if (refundStatus === 'cancelled') return 'cancelled';

  const status = order.tracking_status || order.status;
  switch (status?.toLowerCase()) {
    case 'delivered':
      return 'delivered';
    case 'in_transit':
    case 'out_for_delivery':
      return 'in-transit';
    case 'cancelled':
    case 'refunded':
      return 'cancelled';
    case 'error':
    case 'failure':
    case 'purchase_failed':
      return 'error';
    case 'purchase_pending':
      return 'warning';
    default:
      return 'purchased';
  }
}

/** Human-readable status label, refund state first, then tracking, then status. */
export function getStatusLabel(order: OrderStatusFields): string {
  const refundStatus = getEffectiveRefundStatus(order);
  if (refundStatus === 'submitted') return 'Refund Submitted';
  if (refundStatus === 'refunded') return 'Refunded';
  if (refundStatus === 'rejected') return 'Refund Rejected';
  if (refundStatus === 'cancelled') return 'Cancelled';

  if (order.tracking_status_label) return order.tracking_status_label;
  if (order.tracking_status) {
    return order.tracking_status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return order.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
